﻿import React, {
    useEffect,
    useState,
    useCallback,
    useImperativeHandle,
    forwardRef,
    useRef
} from "react";
import { Slider, Button, Icon } from "@blueprintjs/core";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropUtils";
import PropTypes from "prop-types";
import "./ImageCropper.css";
import { useDebounce } from "../../hooks/useDebounce";
import './DocumentViewer.css';
import './MediaList.css';
import './MediaPlaceholder.css';

const DEFAULT_ASPECT = 1.33334;
const ONE_TO_ONE_ASPECT = 1;
const LANDSCAPE_ASPECT = 1.7778;
const TEMP_ASPECT = 1.00001;

// The crop ratios are tracked by the discrete `key`, not the numeric `value`, so the
// active button is chosen by identity rather than by comparing floating-point aspect
// numbers. `value` is only ever handed to react-easy-crop; it is never compared.
const ASPECT_OPTIONS = [
    { key: "ONE_TO_ONE", label: "1:1", value: ONE_TO_ONE_ASPECT },
    { key: "DEFAULT", label: "4:3", value: DEFAULT_ASPECT },
    { key: "LANDSCAPE", label: "16:9", value: LANDSCAPE_ASPECT }
];

const DEFAULT_ASPECT_KEY = "DEFAULT";

export const ImageCropper = forwardRef(({ file, saving, loading }, ref) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [liveZoom, setLiveZoom] = useState(1);
    const [liveRotation, setLiveRotation] = useState(0);
    const [finalZoom, setFinalZoom] = useState(1);
    const [finalRotation, setFinalRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedPreview, setCroppedPreview] = useState(null);
    const lastPreviewUrl = useRef(null);
    const [aspect, setAspect] = useState(DEFAULT_ASPECT);
    const [aspectKey, setAspectKey] = useState(DEFAULT_ASPECT_KEY);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const DEBOUNCE_DELAY = 500;

    useEffect(() => {
        if (!file?.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = () => setImageSrc(reader.result);
        reader.readAsDataURL(file);
    }, [file]);

    const updatePreview = useCallback(async (zoom = finalZoom, rotation = finalRotation) => {
        if (!imageSrc || !croppedAreaPixels) return;
        // Guard against zero-dimension crop regions (react-easy-crop can emit these
        // on the very first render before the image layout has settled, which would
        // otherwise cause getCroppedImg to reject with "Canvas is empty").
        if (!croppedAreaPixels.width || !croppedAreaPixels.height) return;
        const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, zoom, getMimeType());
        const newUrl = URL.createObjectURL(blob);

        if (lastPreviewUrl.current) URL.revokeObjectURL(lastPreviewUrl.current);
        lastPreviewUrl.current = newUrl;
        setCroppedPreview(newUrl);
    }, [imageSrc, croppedAreaPixels, finalZoom, finalRotation]);

    const debouncedUpdatePreview = useRef(
        useDebounce((zoom, rotation) => {
            setFinalZoom(zoom);
            updatePreview(zoom, rotation);
        }, DEBOUNCE_DELAY)
    ).current;

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
        if (croppedPixels?.width && croppedPixels?.height) {
            setDimensions({
                width: Math.round(croppedPixels.width),
                height: Math.round(croppedPixels.height),
            });
        }
    }, []);

    const resetCrop = () => {
        setCrop({ x: 0, y: 0 });
        setLiveZoom(1);
        setLiveRotation(0);
        setFinalZoom(1);
        setFinalRotation(0);
        if (lastPreviewUrl.current) {
            URL.revokeObjectURL(lastPreviewUrl.current);
            lastPreviewUrl.current = null;
        }
    };

    useImperativeHandle(ref, () => ({
        async getCroppedImage() {
            if (!imageSrc || !croppedAreaPixels) return null;

            return getCroppedImg(imageSrc, croppedAreaPixels, finalRotation, finalZoom, getMimeType());
        },
        reset: resetCrop,
        refreshAspect() {
            setAspect((prevAspect) => {
                const tempAspect = aspectKey === "ONE_TO_ONE" ? TEMP_ASPECT : ONE_TO_ONE_ASPECT;
                setTimeout(() => setAspect(prevAspect), 0);
                return tempAspect;
            });
        }
    }));

    const getMimeType = () => {
        if (!file?.type) return 'image/jpeg';
        const type = file.type.toLowerCase();
        if (type === 'image/png' || type === 'image/jpeg') {
            return type;
        }
        return 'image/jpeg';
    }

    return (
        <div className="image-cropper">
            <div className="image-cropper-workspace">
                <div className="image-cropper-crop-container">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={liveZoom}
                        rotation={liveRotation}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={(value) => {
                            setLiveZoom(value);
                            if (debouncedUpdatePreview) {
                                debouncedUpdatePreview(value, finalRotation);
                            }
                        }}
                        onRotationChange={setLiveRotation}
                        onCropComplete={onCropComplete}
                        restrictPosition={true}
                        onMediaLoaded={() => updatePreview(finalZoom, finalRotation)}
                    />
                    {saving && <div className="image-cropper-overlay" />}
                </div>

                <div className="image-cropper-preview">
                    <div className="image-cropper-preview-header">
                        <span>Preview</span>
                    </div>

                    <div className="image-cropper-preview-box">
                        {croppedPreview && <img src={croppedPreview} alt="Preview" />}
                        {saving && <div className="image-cropper-preview-overlay" />}
                    </div>

                    <div className="image-cropper-preview-info">
                        {file?.size && (
                            <span className="image-cropper-preview-size">
                                {dimensions.width} x {dimensions.height} px
                            </span>
                        )}
                    </div>

                    <div className="image-cropper-aspect-controls">
                        <span className="image-cropper-aspect-label">Aspect:</span>
                        <div className="image-cropper-aspect-buttons">
                            {ASPECT_OPTIONS.map((option) => (
                                <Button
                                    key={option.key}
                                    text={option.label}
                                    size="small"
                                    variant={aspectKey === option.key ? "solid" : "minimal"}
                                    intent={aspectKey === option.key ? "primary" : "none"}
                                    onClick={() => {
                                        setAspect(option.value);
                                        setAspectKey(option.key);
                                    }}
                                    style={{ fontSize: '12px' }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="image-cropper-controls">
                <h5 className="image-cropper-controls-title">Adjust Image</h5>

                <div className="image-cropper-controls-group">
                    <div className="image-cropper-control">
                        <Icon icon="zoom-in" className="image-cropper-control-icon" />
                        <Slider
                            min={1}
                            max={3}
                            value={liveZoom}
                            stepSize={0.1}
                            labelRenderer={false}
                            onChange={setLiveZoom}
                            onRelease={(value) => {
                                setFinalZoom(value);
                                updatePreview(value, finalRotation);
                            }}
                            disabled={saving || loading}
                        />
                        <span className="image-cropper-slider-value">{liveZoom.toFixed(1)}x</span>
                    </div>

                    <div className="image-cropper-control">
                        <Icon icon="refresh" className="image-cropper-control-icon" />
                        <Slider
                            min={0}
                            max={360}
                            value={liveRotation}
                            stepSize={1}
                            labelRenderer={false}
                            onChange={setLiveRotation}
                            onRelease={(value) => {
                                setFinalRotation(value);
                                updatePreview(finalZoom, value);
                            }}
                            disabled={saving || loading}
                        />
                        <span className="image-cropper-slider-value">{liveRotation}°</span>
                    </div>
                </div>

                <div className="image-cropper-actions">
                    <Button
                        icon="reset"
                        text="Reset"
                        onClick={resetCrop}
                        disabled={saving || loading}
                        variant="minimal"
                        className="image-cropper-action-btn"
                    />
                </div>
            </div>

        </div>
    );
});

ImageCropper.propTypes = {
    file: PropTypes.instanceOf(File),
    saving: PropTypes.bool,
    loading: PropTypes.bool,
};
